import React, { useState, useEffect } from 'react';
import {
  Box,
  IconButton,
  Button,
  Paper,
  InputBase,
  Tooltip,
  Collapse,
} from '@mui/material';
import {
  Search as SearchIcon,
  Clear as ClearIcon,
  AddCircleOutline as AddIcon,
} from '@mui/icons-material';
import { colors, fonts, spacing, borderRadius } from '../../constants';

const SearchAndFilter = ({
  onSearchChange,
  // Backwards-compatible props: some pages use `searchQuery`, others use `searchValue`
  searchValue,
  searchQuery,
  placeholder = 'Search queries...',
  defaultExpanded = false,
  showRaiseQueryButton = false,
  onRaiseQueryClick,
  raiseQueryButtonText = 'Raise New Query',
}) => {
  const effectiveSearchValue = searchValue ?? searchQuery ?? '';
  const [localSearchQuery, setLocalSearchQuery] = useState(effectiveSearchValue);
  const [showSearchBar, setShowSearchBar] = useState(defaultExpanded);
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);

  useEffect(() => {
    setShowSearchBar(defaultExpanded);
    setIsExpanded(defaultExpanded);
  }, [defaultExpanded]);

  useEffect(() => {
    setLocalSearchQuery(effectiveSearchValue);
  }, [effectiveSearchValue]);

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setLocalSearchQuery(value);
    if (onSearchChange) {
      onSearchChange(value);
    }
  };

  return (
    <Box sx={{ mb: spacing.md, width: '100%' }}>
      <Paper
        elevation={0}
        sx={{
          p: { xs: spacing.xs, md: spacing.sm },
          borderRadius: borderRadius.lg,
          boxShadow: '0 2px 8px rgba(15, 63, 128, 0.06), 0 1px 2px rgba(0, 0, 0, 0.04)',
          background: 'linear-gradient(135deg, #ffffff 0%,rgba(30, 41, 59, 0.04) 100%)',
          border: `1px solid ${colors.borderLight}`,
          transition: 'all 0.3s ease',
          width: '100%',
        }}
      >
        {/* Header: Search icon, Search bar, and Raise Query button */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: spacing.xs,
            minHeight: '40px',
            width: '100%',
          }}
        >
          {/* Only show toggle button if not default expanded */}
          {!defaultExpanded && (
            <Tooltip title={showSearchBar ? 'Hide Search' : 'Show Search'} arrow>
              <IconButton
                onClick={() => {
                  if (!isExpanded) {
                    setIsExpanded(true);
                  }
                  setShowSearchBar(!showSearchBar);
                }}
                size="small"
                sx={{
                  background: colors.primaryGradient,
                  color: colors.textWhite,
                  width: '36px',
                  height: '36px',
                  borderRadius: borderRadius.md,
                  border: '1px solid transparent',
                  boxShadow: `0 4px 12px ${colors.primaryShadow}`,
                  transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
                  flexShrink: 0,
                  '&:hover': {
                    background: colors.primaryGradientHover,
                    color: colors.textWhite,
                    transform: 'translateY(-1px)',
                    boxShadow: `0 6px 16px ${colors.primaryShadowHover}`,
                    borderColor: 'transparent',
                  },
                  '&:active': {
                    transform: 'translateY(0)',
                  },
                }}
              >
                <SearchIcon sx={{ fontSize: '18px' }} />
              </IconButton>
            </Tooltip>
          )}
          
          {/* Inline Search Bar */}
          {defaultExpanded ? (
            // When default expanded, render directly without Collapse
            <Paper
              elevation={0}
              sx={{
                display: 'flex',
                alignItems: 'center',
                borderRadius: borderRadius.full,
                border: `1.5px solid ${colors.borderDefault}`,
                background: 'linear-gradient(135deg, #ffffff 0%, #f8faff 100%)',
                px: spacing.sm,
                py: 0,
                height: '36px',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                boxShadow: '0 2px 6px rgba(15, 63, 128, 0.06)',
                flex: 1,
                flexGrow: 1,
                minWidth: 0,
                width: '100%',
                '&:focus-within': {
                  borderColor: colors.primary,
                  boxShadow: `0 0 0 3px ${colors.primaryTint}, 0 4px 12px rgba(66, 133, 244, 0.15)`,
                  background: colors.backgroundWhite,
                },
              }}
            >
              <SearchIcon 
                sx={{ 
                  color: colors.textSecondary, 
                  fontSize: '18px', 
                  mr: spacing.xs,
                  flexShrink: 0,
                }} 
              />
              <InputBase
                placeholder={placeholder}
                value={localSearchQuery}
                onChange={handleSearchChange}
                sx={{
                  flex: 1,
                  fontSize: fonts.fontSizeSm,
                  color: colors.textPrimary,
                  fontWeight: fonts.weightMedium,
                  minWidth: 0,
                  width: '100%',
                  '&::placeholder': {
                    color: colors.textTertiary,
                    opacity: 0.7,
                  },
                }}
              />
              {localSearchQuery && (
                <Tooltip title="Clear search" arrow>
                  <IconButton
                    size="small"
                    onClick={() => {
                      setLocalSearchQuery('');
                      if (onSearchChange) onSearchChange('');
                    }}
                    sx={{
                      color: colors.textSecondary,
                      width: '28px',
                      height: '28px',
                      flexShrink: 0,
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        backgroundColor: colors.primaryTint,
                        color: colors.primary,
                        transform: 'scale(1.1)',
                      },
                    }}
                  >
                    <ClearIcon sx={{ fontSize: '16px' }} />
                  </IconButton>
                </Tooltip>
              )}
            </Paper>
          ) : (
            // When not default expanded, use Collapse for animation
            <Collapse 
              in={isExpanded && showSearchBar} 
              orientation="horizontal"
              sx={{ 
                flex: 1, 
                flexGrow: 1, 
                minWidth: 0,
                width: '100%',
                // Make horizontal Collapse take full available width
                '& .MuiCollapse-wrapper, & .MuiCollapse-wrapperInner': {
                  width: '100%',
                },
              }}
            >
              <Paper
                elevation={0}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  borderRadius: borderRadius.full,
                  border: `1.5px solid ${colors.borderDefault}`,
                  background: 'linear-gradient(135deg, #ffffff 0%, #f8faff 100%)',
                  px: spacing.sm,
                  py: 0,
                  height: '36px',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  boxShadow: '0 2px 6px rgba(15, 63, 128, 0.06)',
                  width: '100%',
                  flexGrow: 1,
                  minWidth: 0,
                  '&:focus-within': {
                    borderColor: colors.primary,
                    boxShadow: `0 0 0 3px ${colors.primaryTint}, 0 4px 12px rgba(66, 133, 244, 0.15)`,
                    background: colors.backgroundWhite,
                  },
                }}
              >
                <SearchIcon 
                  sx={{ 
                    color: colors.textSecondary, 
                    fontSize: '18px', 
                    mr: spacing.xs,
                    flexShrink: 0,
                  }} 
                />
                <InputBase
                  placeholder={placeholder}
                  value={localSearchQuery}
                  onChange={handleSearchChange}
                  sx={{
                    flex: 1,
                    fontSize: fonts.fontSizeSm,
                    color: colors.textPrimary,
                    fontWeight: fonts.weightMedium,
                    minWidth: 0,
                    '&::placeholder': {
                      color: colors.textTertiary,
                      opacity: 0.7,
                    },
                  }}
                />
                {localSearchQuery && (
                  <Tooltip title="Clear search" arrow>
                    <IconButton
                      size="small"
                      onClick={() => {
                        setLocalSearchQuery('');
                        if (onSearchChange) onSearchChange('');
                      }}
                      sx={{
                        color: colors.textSecondary,
                        width: '28px',
                        height: '28px',
                        flexShrink: 0,
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          backgroundColor: colors.primaryTint,
                          color: colors.primary,
                          transform: 'scale(1.1)',
                        },
                      }}
                    >
                      <ClearIcon sx={{ fontSize: '16px' }} />
                    </IconButton>
                  </Tooltip>
                )}
              </Paper>
            </Collapse>
          )}

          {showRaiseQueryButton && onRaiseQueryClick && (
            <Button
              variant="contained"
              startIcon={<AddIcon sx={{ fontSize: '18px' }} />}
              onClick={onRaiseQueryClick}
              sx={{
                background: colors.primaryGradient,
                color: colors.textWhite,
                textTransform: 'none',
                borderRadius: borderRadius.button,
                padding: `${spacing.xs} ${spacing.md}`,
                fontWeight: fonts.weightSemiBold,
                fontSize: fonts.fontSizeSm,
                height: '36px',
                boxShadow: `0 4px 12px ${colors.primaryShadow}`,
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                whiteSpace: 'nowrap',
                flexShrink: 0,
                border: '1px solid transparent',
                '&:hover': {
                  background: colors.primaryGradientHover,
                  boxShadow: `0 6px 16px ${colors.primaryShadowHover}`,
                  transform: 'translateY(-1px)',
                },
                '&:active': {
                  transform: 'translateY(0)',
                },
              }}
            >
              {raiseQueryButtonText}
            </Button>
          )}
        </Box>
      </Paper>
    </Box>
  );
};

export default SearchAndFilter;


